import { Chat } from '@ai-sdk/angular';
import { Injectable, NgZone, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  DefaultChatTransport,
  lastAssistantMessageIsCompleteWithToolCalls,
} from 'ai';
import { AnimationOptions } from 'ngx-lottie';
import { catchError, firstValueFrom, take } from 'rxjs';
import { environment } from '../../environments/environment';
import { CompanionAdapter } from '../adapters/companion.adapter';
import {
  ApplyTalentpoolSearchResult,
  CompanionTalentpoolSearchToolOutput,
  TalentpoolAdapter,
} from '../adapters/talentpool.adapter';
import { AppState } from '../app.reducer';
import { CompanionRole } from '../components/companion/companion.enum';
import { generateCompanionJobChanges } from '../components/companion/companion.functions';
import { CompanionJobResponse } from '../components/companion/companion.interface';
import { ApplicantSkillLevel } from '../enums/applicant-skill-level.enum';
import { LanguageLevelCode } from '../enums/language-level-code.enum';
import { SlotType } from '../enums/slot-type.enum';
import { AuthenticationInterceptor } from '../interceptors/authentication.interceptor';
import { Job } from '../interfaces/job';
import { JobPosting } from '../interfaces/job-posting';
import { refreshToken, refreshTokenSuccess } from '../store/auth/auth.actions';
import { selectToken } from '../store/auth/auth.reducer';
import { selectConnection } from '../store/connection/connection.reducer';
import { selectPreselectSlotType } from '../store/subscription/subscription.reducer';
import { CompanionService } from './companion.service';
import { TalentpoolFilterService } from './filter/talentpool-filter.service';

/**
 * Shared KIRA chat state used by the companion sidebar and the job prompting modal.
 */
@Injectable({
  providedIn: 'root',
})
export class CompanionChatService {
  private readonly actions$ = inject(Actions);
  private readonly authenticationInterceptor = inject(
    AuthenticationInterceptor,
  );
  private readonly companionAdapter = inject(CompanionAdapter);
  private readonly companionService = inject(CompanionService);
  private readonly talentpoolFilterService = inject(TalentpoolFilterService);
  private readonly talentpoolAdapter = inject(TalentpoolAdapter);
  private readonly router = inject(Router);
  private readonly translocoService = inject(TranslocoService);
  private readonly store = inject(Store<AppState>);
  private readonly ngZone = inject(NgZone);

  readonly MAX_LENGTH_CHARACTER_LIMIT = 3000;

  private lastAppliedSearchSignature: string | null = null;

  readonly loading = signal(false);
  readonly errorWhileAnalyzing = signal(false);
  readonly errorWhileReplying = signal(false);
  readonly typing = signal(false);
  readonly textValue = signal('');
  readonly scrollRequest = signal(0);
  readonly focusRequest = signal(0);

  readonly loadingLottie: AnimationOptions = {
    path: './assets/animations/lottie-animations/companion-sparkles.json',
  };
  readonly errorLottie: AnimationOptions = {
    path: './assets/animations/lottie-animations/companion-error.json',
  };

  private readonly connection = this.store.selectSignal(selectConnection);

  private readonly chatInstance = signal<Chat>(this.createChat());

  /**
   * Current shared chat instance. Reading this in templates/computeds
   * tracks chat resets via the underlying signal.
   * @returns {Chat} Active chat
   */
  get chat(): Chat {
    return this.chatInstance();
  }

  readonly isKiraBusy = computed(() => {
    const status = this.chatInstance().status;
    return (status !== 'ready' && status !== 'error') || this.loading();
  });

  readonly hasMessages = computed(
    () => this.chatInstance().messages.length > 0,
  );

  /**
   * Index of the first message visible in the prompting modal.
   * `null` when the modal session is inactive.
   */
  readonly promptingSessionStartIndex = signal<number | null>(null);

  /**
   * Pending start index captured when navigating to the job editor from chat,
   * applied when the prompting modal opens.
   */
  readonly pendingPromptingSessionStartIndex = signal<number | null>(null);

  readonly promptingMessages = computed(() => {
    const messages = this.chatInstance().messages;
    const start = this.promptingSessionStartIndex();
    if (start == null) {
      return messages;
    }
    return messages.slice(Math.min(start, messages.length));
  });

  readonly hasPromptingMessages = computed(
    () => this.promptingMessages().length > 0,
  );

  readonly canSubmitMessage = computed(() => {
    const text = this.textValue();
    return (
      !this.isKiraBusy() &&
      text.trim().length > 0 &&
      text.length <= this.MAX_LENGTH_CHARACTER_LIMIT
    );
  });

  constructor() {
    this.companionService.templateJobLoaded
      .pipe(takeUntilDestroyed())
      .subscribe((job: CompanionJobResponse) => {
        this.onTemplateJobLoaded(job);
      });

    this.companionService.sendMessage
      .pipe(takeUntilDestroyed())
      .subscribe((message) => {
        this.companionService.openCompanion();
        this.requestScroll();
        this.requestFocus();
        const isTalentpoolStart = message === '<talentpool_search_start>';
        void this.sendMessage(message, false, isTalentpoolStart);
      });

    this.companionService.newChat.pipe(takeUntilDestroyed()).subscribe(() => {
      this.resetChat();
    });

    this.companionService.leftJobOfferCreation
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.endPromptingSession();
      });
  }

  /**
   * Recreates the shared chat instance (clears history).
   * @returns {void}
   */
  resetChat(): void {
    this.lastAppliedSearchSignature = null;
    this.promptingSessionStartIndex.set(null);
    this.pendingPromptingSessionStartIndex.set(null);
    this.chatInstance.set(this.createChat());
  }

  /**
   * Starts a prompting-modal message window. Uses a pending index from
   * job-editor navigation when present; otherwise starts after existing messages.
   * @returns {void}
   */
  beginPromptingSession(): void {
    const pending = this.pendingPromptingSessionStartIndex();
    if (pending != null) {
      this.promptingSessionStartIndex.set(pending);
      this.pendingPromptingSessionStartIndex.set(null);
      return;
    }
    this.promptingSessionStartIndex.set(this.chat.messages.length);
  }

  /**
   * Clears the prompting-modal message window (keeps full chat history).
   * @returns {void}
   */
  endPromptingSession(): void {
    this.promptingSessionStartIndex.set(null);
    this.pendingPromptingSessionStartIndex.set(null);
  }

  /**
   * Clears a pending prompting session without opening the modal
   * (companion-initiated job creation continues in the sidebar).
   * @returns {void}
   */
  clearPendingPromptingSession(): void {
    this.pendingPromptingSessionStartIndex.set(null);
  }

  /**
   * Marks the latest user message as the start of the next prompting session
   * (used when KIRA navigates into the job editor).
   * @returns {void}
   */
  capturePendingPromptingSessionStart(): void {
    const messages = this.chat.messages;
    let start = messages.length;
    for (let index = messages.length - 1; index >= 0; index--) {
      if (messages[index].role === 'user') {
        start = index;
        break;
      }
    }
    this.pendingPromptingSessionStartIndex.set(start);
  }

  /**
   * Requests message list scroll-to-bottom in all chat UIs.
   * @returns {void}
   */
  requestScroll(): void {
    this.scrollRequest.update((value) => value + 1);
  }

  /**
   * Requests focus on the active message input.
   * @returns {void}
   */
  requestFocus(): void {
    this.focusRequest.update((value) => value + 1);
  }

  /**
   * Creates a new AI SDK chat bound to the companion message endpoint.
   * @returns {Chat} Configured chat instance
   */
  private createChat(): Chat {
    return new Chat({
      transport: new DefaultChatTransport({
        api: `${environment.apiBaseUrl}/company/companion/message`,
        prepareSendMessagesRequest: async ({
          id,
          messages,
          body,
          trigger,
          messageId,
        }) => {
          const jwt = await this.resolveCompanionJwt();
          return {
            body: {
              id,
              messages,
              trigger,
              messageId,
              ...body,
              jwt,
              role: this.companionService.role(),
              currentRoute: this.companionService.getMessageContextRoute(),
              talentpoolSearchId:
                this.talentpoolFilterService.filters().kiraSearchId,
            },
          };
        },
      }),
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithToolCalls,
      onToolCall: ({ toolCall }) => {
        if (toolCall.dynamic) return;
        switch (toolCall.toolName) {
          case 'generateJsonAnswer':
            this.companionService.jobOfferChange.emit(
              this.resolveGenerateJsonAnswerInput(toolCall.input),
            );
            break;
          case 'navigateToJobEditor':
            void this.handleNavigateToJobEditorTool(toolCall.toolCallId);
            break;
          case 'navigateToTalentpool':
            void this.router.navigate(['/talents']);
            break;
        }
      },
      onFinish: () => {
        this.typing.set(false);
        this.scheduleTalentpoolSearchResultProcessing();
        this.requestScroll();
        this.requestFocus();
      },
      onError: (error) => {
        console.error('Companion stream error', error);
        this.scheduleTalentpoolSearchResultProcessing();
        this.errorWhileReplying.set(true);
        this.typing.set(false);
        this.requestScroll();
        this.requestFocus();
      },
    });
  }

  /**
   * Sends a message to the companion and handles the stream + authentication.
   * @param {string} message The message to send
   * @param {boolean} silent Used to send messages without wrapping in a visible message tag
   * @param {boolean} isTalentpoolSearchStart Whether this starts a talentpool search
   * @returns {Promise<void>}
   */
  async sendMessage(
    message = this.textValue(),
    silent = false,
    isTalentpoolSearchStart = false,
  ): Promise<void> {
    const normalizedMessage = message.trim();
    if (
      this.isKiraBusy() ||
      !normalizedMessage ||
      (!isTalentpoolSearchStart &&
        this.textValue().length > this.MAX_LENGTH_CHARACTER_LIMIT)
    ) {
      return;
    }

    this.errorWhileReplying.set(false);

    if (
      this.router.url.includes('/talents') &&
      this.companionService.role() !== CompanionRole.TALENTPOOL_SEARCH
    ) {
      this.companionService.role.set(CompanionRole.TALENTPOOL_SEARCH);
    }

    let query: string;

    if (isTalentpoolSearchStart) {
      const visibleMessage = this.translocoService.translate(
        'company-tool.snippets.companion.talentpool-search.start-message',
      );
      query = `<message>${visibleMessage}</message>\n<talentpool_search_start>`;
    } else {
      const msg = silent
        ? normalizedMessage
        : `<message>${normalizedMessage}</message>`;

      switch (this.companionService.role()) {
        case CompanionRole.JOB_OFFER_CREATION: {
          const diffs = generateCompanionJobChanges(
            this.companionService.lastCompanionJobPayload(),
            this.companionService.currentCompanionJobPayload(),
          );
          const draftContext = this.companionService.draftJobEditMode()
            ? `<draft_content>${JSON.stringify(this.companionService.currentCompanionJobPayload())}</draft_content>`
            : '';
          query = `
          ${msg}
          ${draftContext}
          <job_changes>
            ${JSON.stringify(diffs)}
          </job_changes>
          <facility_options>
            {added: ${JSON.stringify(this.companionService.addedFacilities())}}
          </facility_options>`;
          break;
        }
        case CompanionRole.TALENTPOOL_SEARCH:
          query = msg;
          break;
        default:
          query = msg;
          break;
      }
    }

    this.textValue.set('');

    this.chat.sendMessage({
      text: query,
    });
    this.requestScroll();
  }

  /**
   * Handles PDF selection and silent companion import.
   * @param {File} file The selected PDF file
   * @returns {void}
   */
  onPdfSelected(file: File): void {
    this.loading.set(true);
    if (this.companionService.promptingModalOpen()) {
      this.companionService.promptingAnalyzing.set(true);
      this.companionService.promptingLoadInProgress.set(false);
      this.companionService.promptingGenerating.set(false);
    }
    const formData = new FormData();
    formData.append('document', file);
    this.companionAdapter
      .parseJobOfferPdf(formData)
      .pipe(
        catchError((error) => {
          this.displayAnalyzingError();
          throw error;
        }),
      )
      .subscribe((response) => {
        this.loading.set(false);
        if (this.companionService.promptingModalOpen()) {
          this.companionService.promptingAnalyzing.set(false);
          this.companionService.promptingLoadInProgress.set(true);
        }
        const message = response?.result?.general?.jobTitle
          ? this.translocoService.translate(
              'company-tool.snippets.companion.tools.load-job-from-pdf.message',
              {
                jobTitle: response?.result?.general?.jobTitle,
              },
            )
          : '';
        void this.sendMessage(
          `<message>${message}</message><file_name>${
            file.name
          }</file_name><pdf_content>${JSON.stringify(
            response?.result ?? response,
          )}</pdf_content>`,
          true,
        );
      });
  }

  /**
   * Handles ATS job selection and silent companion import.
   * @param {JobPosting} job The selected ATS job
   * @returns {void}
   */
  onATSJobSelected(job: JobPosting): void {
    this.companionService.atsJobLoad.emit(job);
    if (this.companionService.promptingModalOpen()) {
      this.companionService.promptingLoadInProgress.set(true);
    }
    const connection = this.connection();
    const connectionName = connection?.connection?.name || '';
    void this.sendMessage(
      `<external_ats_content>${JSON.stringify(
        job,
      )}</external_ats_content><message>🔗 Stelle <b>${
        job.title
      }</b> aus <b>${connectionName}</b> geladen.</message>`,
      true,
    );
  }

  /**
   * Handles template job selection (editor prefills, then templateJobLoaded fires).
   * @param {Job} job The selected template job
   * @returns {void}
   */
  onTemplateJobSelected(job: Job): void {
    if (this.companionService.promptingModalOpen()) {
      this.companionService.promptingLoadInProgress.set(true);
    }
    this.companionService.templateJobLoad.emit(job);
  }

  /**
   * Sends template content to the companion after the editor prefilled from a template.
   * @param {CompanionJobResponse} job The loaded job payload
   * @returns {void}
   */
  onTemplateJobLoaded(job: CompanionJobResponse): void {
    void this.sendMessage(
      `<template_content>${JSON.stringify(
        job,
      )}</template_content><message>📋 Vorlage <b>${
        job.general.jobTitle
      }</b> geladen.</message>`,
      true,
    );
  }

  /**
   * Resolves generateJsonAnswer tool input to the job payload.
   * Backend schema wraps the job as `{ finalState }`; older calls may send the job at the top level.
   * @param {unknown} input Raw tool call input
   * @returns {CompanionJobResponse} Job offer payload for the editor
   */
  private resolveGenerateJsonAnswerInput(input: unknown): CompanionJobResponse {
    if (
      input &&
      typeof input === 'object' &&
      'finalState' in input &&
      (input as { finalState: unknown }).finalState &&
      typeof (input as { finalState: unknown }).finalState === 'object' &&
      !('general' in input)
    ) {
      return (input as { finalState: CompanionJobResponse }).finalState;
    }
    return input as CompanionJobResponse;
  }

  /**
   * Shows a temporary analyzing error state.
   * @returns {void}
   */
  displayAnalyzingError(): void {
    this.loading.set(false);
    this.companionService.promptingAnalyzing.set(false);
    this.companionService.promptingLoadInProgress.set(false);
    this.errorWhileAnalyzing.set(true);
    setTimeout(() => {
      this.errorWhileAnalyzing.set(false);
    }, 5000);
  }

  /**
   * Parses a message part by a given tag from the message string.
   * @param {string} message Message to parse
   * @param {string} tag Tag to parse
   * @returns {string} Parsed message part
   */
  parseMessagePart(message: string, tag: string = 'message'): string {
    const match = message.match(
      new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'i'),
    );
    return match ? match[1].trim().replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') : '';
  }

  /**
   * Parses a markdown message to HTML.
   * @param {string} message The message to parse
   * @returns {string} The parsed message
   */
  parseMarkdown(message: string): string {
    return message.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
  }

  /**
   * Handles clicks on companionAction / companionRoute elements inside messages.
   * @param {Event} event The click event
   * @returns {void}
   */
  handleMessageClick(event: Event): void {
    const target = event.target as HTMLElement;
    const liElement = target.closest('li');

    if (!liElement) {
      return;
    }

    if (liElement.classList.contains('companionAction')) {
      const searchId = liElement.getAttribute('data-search-id');
      if (searchId) {
        this.talentpoolAdapter.replaySearch(searchId).subscribe((result) => {
          this.applyTalentpoolSearchResult(result);
        });
        return;
      }

      const prompt =
        liElement.getAttribute('data-prompt') ?? liElement.textContent ?? '';
      void this.sendMessage(prompt.trim());
      return;
    }

    if (liElement.classList.contains('companionRoute')) {
      const targetRoute = liElement.getAttribute('data-route');
      if (this.companionService.role() === CompanionRole.JOB_OFFER_CREATION) {
        window.open(targetRoute, '_blank');
      } else if (targetRoute) {
        void this.router.navigate([targetRoute]);
      }
    }
  }

  /**
   * Resolves a JWT for companion API calls, refreshing when expired.
   * @returns {Promise<string>} JWT token
   */
  private async resolveCompanionJwt(): Promise<string> {
    const tokenSignal = this.store.selectSignal(selectToken);
    let token = tokenSignal();
    if (this.authenticationInterceptor.tokenExpired(token)) {
      this.store.dispatch(refreshToken({ polling: false }));
      token = (
        await firstValueFrom(
          this.actions$.pipe(ofType(refreshTokenSuccess), take(1)),
        )
      ).token;
    }
    return token;
  }

  /**
   * Client-side tool: navigate to the job editor and report guard blocks to KIRA.
   * @param {string} toolCallId Tool call id for addToolOutput
   * @returns {Promise<void>}
   */
  private async handleNavigateToJobEditorTool(
    toolCallId: string,
  ): Promise<void> {
    const output = await this.executeNavigateToJobEditor();
    this.chat.addToolOutput({
      tool: 'navigateToJobEditor',
      toolCallId,
      output,
    });
  }

  /**
   * Navigates to /jobs/new unless already in job-offer creation.
   * Auto-selects the highest available slot and returns it for KIRA to mention.
   * @returns {Promise<{navigated: boolean; alreadyOpen?: boolean; blockedReason?: string | null; selectedSlotType?: SlotType; selectedSlotTypeLabel?: string}>}
   */
  private async executeNavigateToJobEditor(): Promise<{
    navigated: boolean;
    alreadyOpen?: boolean;
    blockedReason?: 'unsaved_changes' | 'navigation_failed' | null;
    selectedSlotType?: SlotType;
    selectedSlotTypeLabel?: string;
  }> {
    const selectedSlotType = this.store.selectSignal(selectPreselectSlotType)();
    const selectedSlotTypeLabel = this.translocoService.translate(
      `company-tool.snippets.slot-selector.selection.${selectedSlotType}.title`,
    );

    if (this.companionService.role() === CompanionRole.JOB_OFFER_CREATION) {
      return {
        navigated: true,
        alreadyOpen: true,
        blockedReason: null,
        selectedSlotType,
        selectedSlotTypeLabel,
      };
    }

    try {
      // Mark companion entry before navigate so the job editor can skip create modals
      // (ngAfterViewInit may run before this promise resolves).
      this.capturePendingPromptingSessionStart();
      const navigated = await this.router.navigate(['/jobs/new']);
      if (navigated) {
        this.companionService.role.set(CompanionRole.JOB_OFFER_CREATION);
        this.companionService.openCompanion(false);
        return {
          navigated: true,
          blockedReason: null,
          selectedSlotType,
          selectedSlotTypeLabel,
        };
      }
      this.clearPendingPromptingSession();
      return { navigated: false, blockedReason: 'unsaved_changes' };
    } catch {
      this.clearPendingPromptingSession();
      return { navigated: false, blockedReason: 'navigation_failed' };
    }
  }

  /** Retries applying KIRA search tool output after the chat stream finishes. */
  private scheduleTalentpoolSearchResultProcessing(): void {
    this.ngZone.run(() => {
      const attempt = (allowPreliminary = false) =>
        this.processTalentpoolSearchToolResults(
          this.chat.messages,
          allowPreliminary,
        );

      attempt(false);
      queueMicrotask(() => attempt(true));
      setTimeout(() => attempt(true), 0);
    });
  }

  /** Reads applyTalentpoolSearch tool parts from assistant messages and applies new results. */
  private processTalentpoolSearchToolResults(
    messages: ReadonlyArray<(typeof this.chat.messages)[number]> = this.chat
      .messages,
    allowPreliminary = false,
  ): void {
    if (this.companionService.role() !== CompanionRole.TALENTPOOL_SEARCH) {
      return;
    }

    for (const message of [...messages].reverse()) {
      if (message.role !== 'assistant') {
        continue;
      }

      for (const part of [...(message.parts ?? [])].reverse()) {
        const result = this.extractApplyTalentpoolSearchResult(
          part,
          allowPreliminary,
        );
        if (!result) {
          continue;
        }

        const signature = `${result.searchId}:${JSON.stringify(result.filterChips)}`;
        if (signature === this.lastAppliedSearchSignature) {
          return;
        }

        this.queueTalentpoolSearchApplication(result);
        return;
      }
    }
  }

  /** Loads the saved search by id and syncs filters with the talent pool view. */
  private queueTalentpoolSearchApplication(
    toolOutput: CompanionTalentpoolSearchToolOutput,
  ): void {
    this.talentpoolAdapter
      .replaySearch(toolOutput.searchId)
      .pipe(take(1))
      .subscribe({
        next: (result) => this.applyTalentpoolSearchResult(result),
        error: () => {
          this.errorWhileReplying.set(true);
        },
      });
  }

  /** Returns parsed applyTalentpoolSearch output from a chat tool part, if present. */
  private extractApplyTalentpoolSearchResult(
    part: unknown,
    allowPreliminary = false,
  ): CompanionTalentpoolSearchToolOutput | null {
    const toolPart = part as {
      type?: string;
      toolName?: string;
      state?: string;
      preliminary?: boolean;
      output?: CompanionTalentpoolSearchToolOutput;
    };

    if (
      toolPart.state !== 'output-available' ||
      (!allowPreliminary && toolPart.preliminary) ||
      !toolPart.output?.searchId ||
      toolPart.output.talentpoolEmpty
    ) {
      return null;
    }

    const isApplyTalentpoolSearchTool =
      toolPart.type === 'tool-applyTalentpoolSearch' ||
      (toolPart.type === 'dynamic-tool' &&
        toolPart.toolName === 'applyTalentpoolSearch');

    return isApplyTalentpoolSearchTool ? toolPart.output : null;
  }

  /** Writes KIRA search filters to the shared talent pool filter state. */
  private applyTalentpoolSearchResult(
    result: ApplyTalentpoolSearchResult,
  ): void {
    this.errorWhileReplying.set(false);
    this.lastAppliedSearchSignature = `${result.searchId}:${JSON.stringify(result.filterChips)}`;
    const filters = result.filters;
    this.talentpoolFilterService.setFilters({
      jobFieldCategorySearch: filters.jobFieldCategorySearch,
      skillFilters: filters.skillFilters.map((skillFilter) => ({
        skillId: skillFilter.skillId,
        skillName: skillFilter.skillName,
        minLevel:
          (skillFilter.minLevel as ApplicantSkillLevel) ??
          ApplicantSkillLevel.FUNDAMENTAL,
      })),
      location: filters.location,
      languageFilters: filters.languageFilters.map((languageFilter) => ({
        code: languageFilter.code,
        label: languageFilter.code.toUpperCase(),
        minLevel: languageFilter.minLevel as LanguageLevelCode | undefined,
      })),
      minYearsExperience: filters.minYearsExperience,
      entryDateFrom: filters.entryDateFrom,
      kiraSearchId: filters.kiraSearchId ?? result.searchId,
      kiraFilterChips: result.filterChips,
    });
    this.talentpoolFilterService.notifyFiltersApplied();

    if (!this.router.url.includes('/talents')) {
      void this.router.navigate(['/talents']);
    }
  }
}
